import React, { useEffect, useState } from 'react';
import { Tooltip, TextField, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';

interface LinkItemProps {
  link: any;
  index: number;
  onUpdate: (linkId: string, date: Date) => Promise<void>;
  onDelete: (linkId: string) => void;
  tabOptions?: { tabId: number; tabName: string }[];
}

const LinkItem = React.memo(({ link, index, onUpdate, onDelete, tabOptions }: LinkItemProps) => {
  const [localExpiry, setLocalExpiry] = useState<Date | null>(new Date(link.expirationTime));
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (link.expirationTime) {
      setLocalExpiry(new Date(link.expirationTime));
    }
  }, [link.expirationTime]);

  const [selectedTabName, setSelectedTabName] = useState<string>('');

  const urlObj = new URL(window.location.href);
  urlObj.searchParams.delete('tabName');
  urlObj.searchParams.set('dashboard', link.linkId);
  if (selectedTabName) {
    urlObj.searchParams.set('tabName', selectedTabName);
  }

  /** Full URL for this open-link, with the chosen tab appended when provided */
  const search = Array.from(urlObj.searchParams.entries())
    .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
    .join('&');
  const fullUrl = `${urlObj.origin}${urlObj.pathname}${search ? '?' + search : ''}${urlObj.hash}`;

  const handleCopy = () => {
    const url = fullUrl;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      })
      .catch(() => {
        console.error('Failed to copy: ');
      });
  };

  const handleDateChange = async (date: Date | null) => {
    if (!date) return;

    if (date.getTime() === localExpiry?.getTime()) return;

    const prevDate = localExpiry ?? new Date(link.expirationTime);

    try {
      setLocalExpiry(date);
      await onUpdate(link.linkId, date);
    } catch (error) {
      setLocalExpiry(prevDate);
    }
  };

  const CustomTextField = React.useCallback(
    (params: any) => (
      <TextField
        {...params}
        sx={{
          '& .MuiInputBase-root': {
            height: '30px',
            minHeight: '30px',
            width: '130px',
            marginLeft: '5px',
            marginRight: '5px',
          },
        }}
      />
    ),
    [],
  );

  return (
    <div className="link-expire-date">
      <div className="link-expire-span">
        <div>
          <span className="link-expire-sapn-link">Link {index + 1} :</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {tabOptions && tabOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: '150px', marginRight: '1rem' }}>
              <InputLabel sx={{ fontSize: '13px', top: '-4px' }}>Opening Tab</InputLabel>
              <Select
                value={selectedTabName}
                label="Opening Tab"
                onChange={(e) => setSelectedTabName(e.target.value)}
                sx={{ fontSize: '13px', height: '30px' }}
              >
                <MenuItem
                  value=""
                  sx={{ fontSize: '13px', fontStyle: 'italic', color: 'text.secondary' }}
                >
                  No specific tab
                </MenuItem>
                {tabOptions.map((tab: { tabId: number; tabName: string }) => (
                  <MenuItem key={tab.tabId} value={tab.tabName} sx={{ fontSize: '13px' }}>
                    {tab.tabName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <span>Expiry Date:</span>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              enableAccessibleFieldDOMStructure={false}
              value={localExpiry}
              onChange={handleDateChange}
              minDate={moment().startOf('day').toDate()}
              maxDate={moment().add(90, 'days').endOf('day').toDate()}
              slots={{
                textField: CustomTextField,
              }}
            />
          </LocalizationProvider>

          <Tooltip
            title="Delete Link"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <img
              src="/delete_red.png"
              alt="Delete Link"
              style={{ width: '17px', height: '17px', marginRight: '5px', cursor: 'pointer' }}
              onClick={() => onDelete(link.linkId)}
            />
          </Tooltip>
        </div>
      </div>

      <div className="share-link-copy-paste-container">
        <span className="shared-link-url">{fullUrl}</span>
        <div style={{ position: 'relative' }} className="copy-container" onClick={handleCopy}>
          {isCopied ? (
            <div className="copied">
              <img className="tick-icon" src="/tick-icon.png" alt="Tick Icon" />
              <span>copied</span>
            </div>
          ) : (
            <div className="copy">
              <img src="/copy-image2.png" alt="Copy Icon" />
              <span>copy</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default LinkItem;
